import { useState } from 'react';
import { useApp } from '../App.jsx';
import { Dialog, Icon, Money, MoneyInput, PersonDot, StatusBadge } from './ui.jsx';
import { monthLabel, addMonths, dateLabel } from '../lib/calendar.js';
import { fmtPlain, parseMoney, fmt } from '../lib/money.js';
import { setExpenseActual, setMonthNote } from '../lib/actions.js';

function ExpenseActual({ month, e }) {
  const { commit } = useApp();
  const [text, setText] = useState(e.override === null ? '' : fmtPlain(e.override));
  const parsed = parseMoney(text, { allowEmpty: true, allowNegative: false });
  const save = () => {
    if (!parsed.ok) return;
    const v = parsed.value;
    if ((v ?? null) === (e.override ?? null)) return;
    commit(`${monthLabel(month)}: ${e.name} ${v === null ? 'back to budget' : fmt(v)}`, (s) => setExpenseActual(s, month, e.id, v));
  };
  return (
    <tr>
      <td className="left">{e.name}<div className="small muted">{e.category}{e.owner !== 'joint' ? '' : ' · joint'}</div></td>
      <td><Money value={e.budget} /></td>
      <td style={{ width: 150 }}>
        <MoneyInput ariaLabel={`${e.name} actual amount`} value={text} parsed={parsed} placeholder={fmtPlain(e.budget)} onChange={setText}
          onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); save(); } }} />
      </td>
      <td style={{ width: 60 }}>
        <button type="button" className="btn ghost small" onClick={save} disabled={!parsed.ok || (parsed.value ?? null) === (e.override ?? null)}>Save</button>
      </td>
    </tr>
  );
}

export default function MonthDialog({ month: initial, onClose }) {
  const { model, commit, openReview, go, data } = useApp();
  const [month, setMonth] = useState(initial);
  const m = model.byKey[month];
  const [note, setNote] = useState(m?.note || '');
  const switchTo = (k) => { setMonth(k); setNote(model.byKey[k]?.note || ''); };
  if (!m) return <Dialog title={monthLabel(month)} onClose={onClose}><p className="dim">This month is outside the tracked range.</p></Dialog>;
  const prev = addMonths(month, -1);
  const next = addMonths(month, 1);
  const saveNote = () => { if (note !== (m.note || '')) commit(`${monthLabel(month)}: note`, (s) => setMonthNote(s, month, note)); };

  return (
    <Dialog
      wide
      title={(
        <span className="row">
          <button type="button" className="btn ghost icon small" aria-label="Previous month" disabled={!model.byKey[prev]} onClick={() => { saveNote(); switchTo(prev); }}><Icon name="chevronL" /></button>
          {monthLabel(month)}
          <button type="button" className="btn ghost icon small" aria-label="Next month" disabled={!model.byKey[next]} onClick={() => { saveNote(); switchTo(next); }}><Icon name="chevronR" /></button>
          <StatusBadge status={m.status} />
        </span>
      )}
      onClose={() => { saveNote(); onClose(); }}
    >
      <section>
        <h3 className="small dim" style={{ fontWeight: 650, marginBottom: 6 }}>PAYDAYS</h3>
        <table className="plain">
          <thead><tr><th>Who</th><th className="left">Status</th><th className="hide-sm">Payday</th><th>Take-home</th><th>To savings</th><th /></tr></thead>
          <tbody>
            {m.people.map((e) => (
              <tr key={e.personId}>
                <td><span className="pill-person"><PersonDot color={e.color} />{e.name}</span></td>
                <td className="left"><StatusBadge status={e.status} source={e.source} /></td>
                <td className="hide-sm dim">{dateLabel(e.payday, { weekday: false })}</td>
                <td><Money value={e.actual.net} /></td>
                <td><Money value={e.actual.contribution} /></td>
                <td><button type="button" className={`btn small ${e.status === 'pending' ? 'primary' : ''}`} onClick={() => openReview(month, e.personId)}>{e.status === 'pending' ? 'Review' : 'Open'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3 className="small dim" style={{ fontWeight: 650, marginBottom: 6 }}>EXPENSES THIS MONTH</h3>
        {!m.expenses.length ? <p className="muted small">No expenses budgeted. <button type="button" className="linkbtn" onClick={() => { onClose(); go('budget'); }}>Add them in Budget</button></p> : (
          <>
            <p className="small muted" style={{ marginBottom: 6 }}>If a bill came in different from the budget, type the real amount. The difference comes out of (or goes into) this month's savings without changing anyone's confirmed payday.</p>
            <table className="plain">
              <thead><tr><th>Expense</th><th>Budget</th><th>Actual</th><th /></tr></thead>
              <tbody>
                {m.expenses.map((e) => <ExpenseActual key={`${month}${e.id}`} month={month} e={e} />)}
                <tr className="total"><td>Total</td><td><Money value={m.totalBudget} /></td><td><Money value={m.totalBudget + m.variance} /></td><td /></tr>
              </tbody>
            </table>
            {m.variance !== 0 && <p className="small" style={{ marginTop: 6 }}>Over budget by <Money value={m.variance} /> this month.</p>}
          </>
        )}
      </section>

      {m.transactions.length > 0 && (
        <section>
          <h3 className="small dim" style={{ fontWeight: 650, marginBottom: 6 }}>MONEY MOVED IN OR OUT OF SAVINGS</h3>
          <table className="plain"><tbody>{m.transactions.map((t) => <tr key={t.id}><td className="dim">{dateLabel(t.date, { weekday: false })}</td><td className="left">{t.label}</td><td><Money value={t.amount} sign /></td></tr>)}</tbody></table>
        </section>
      )}

      <section className="kv">
        <span>Expected to save</span><Money value={m.expectedSavings} />
        <span className="strong">Saved</span><Money value={m.savings} className="strong" />
        <span>Balance after this month</span><Money value={m.balance} />
      </section>

      <label className="field">
        <span className="lbl">Notes for {monthLabel(month)}</span>
        <textarea className="input" value={note} onChange={(e) => setNote(e.target.value)} onBlur={saveNote} placeholder="Anything about this month" maxLength={2000} />
      </label>
      {data.people.length === 0 && <p className="muted">No people yet.</p>}
    </Dialog>
  );
}
